import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import { Link } from "react-router";
import type { PublicMapResident, RhythmSky } from "@/shared/contracts";
import { Icon } from "@/shared/ui";
import { BrandLogo } from "@/shared/ui/BrandLogo";
import { GlobeFallback } from "./GlobeFallback";
import type { GlobeCluster, GlobeRegion, GlobeView, PlanetGlobeHandle, PlanetGlobeProps, PlanetSpaceOptions, PlanetSpacePhase } from "./globeTypes";
import { useMapDeps } from "./mapDeps";
import { MapPortrait, SpotChooser } from "./MapResidentCard";
import { nearPlacement, type NearFit } from "./NearPanel";
import { globeBringIntoView, globeCamera, globePinHit, globePinRect, globeRepKey, type GlobeCamera } from "./globeCamera";
import type { PublicMapLayer } from "./publicMap.service";
import { citiesOf, cityRegion, clusterByKey, clustersOf, hasPosition, highlightsOnMarkers, isMergedKey, mergedCluster, mergedKeys, regionFocus, regionLabel, spreadClusters, withCityOnSameName, withImage } from "./publicMapModel";
import { RegionLayer } from "./RegionLayer";
import { useWorldFeed } from "./useWorldFeed";
import { WorldFeedPanel } from "./WorldFeedPanel";
import { areaText, type WorldFeed, type WorldFeedItem } from "./worldFeed";
import { PAN_MS, prefersReducedMotion, type MapCamera } from "@/shared/map/mapCamera";
import { feedHighlights as feedFlashes, moveRoutes } from "../planet3d/feedToGlobe";
import { DEFAULT_DISTANCE } from "../planet3d/globeModel";
import type { GlobeHighlight } from "./globeTypes";
import "./public-map.css";
import "./starfield.css"; // 星空场景（UI5700）：原来接在 public-map.css 末尾，挪成单独一份，层叠顺序不变
import { PlanetStage } from "./PlanetStage";
import { castKey, keepCast, stageCast, stageSky, type StageCast } from "./planetStageModel";

/**
 * 访客首页的星球地图（c84a CR-PUBLIC-WORLD-MAP-01，2026-10-03）：首屏以地图为主，看出居民分别生活在哪、此刻在哪，点进去再认识 TA。
 * 两层：
 * 1. 总览——3D 星球（6796 的 planet3d/PlanetGlobe，按需加载）；WebGL 不可用、它回调 onUnavailable、或系统要求减少动态效果时用 2D 占位 GlobeFallback（同一套 props）；
 * 2. 地区——总览“放大到地区”（onRegion）后切过来：默认高德（RegionLayer → RegionAmap），没配置、加载失败、没 WebGL 时降级成 shared/map 示意底图。
 * 点一处：一位出页面给的就近面板（nearCard，UI-WORLD-LIVING-117-2；117-2b 起登录前后都是它，原来登录后的短卡 MapResidentCard 删了），几位（总览上坐标相等的、挨得近合成的）先出名单（SpotChooser）。Esc、关上、点外面都能关；Esc 和关上把焦点还给原来的标记。
 * 地区层里同一格的几位按宠物 id 错开（spreadClusters，UI-REVIEW110-04），点开、名单、回来接着看都按错开后的那一份。
 * 观察室：地图底边贴一条“星球上的新鲜事”（./WorldFeedPanel，/public/world/feed，feed v4 合同，30 秒轮询、后台不轮询）；
 * 访客（discovery）落地就是全屏星球，底边换成「今天星球上发生的事」上拉面板（页面给的 discovery，UI-WORLD-LIVING-117-1），新鲜事同一个读口交给它；
 * 看着的时候新来的、带片区的事件在总览上闪一下（highlights，第六轮接 6796 的 planet3d/feedToGlobe：首读只记不闪）；就近面板说这一位故事里最靠前的那一件。
 * 地区层与上次点开的那一处记在 sessionStorage（访客自己的 public 作用域），去居民主页再回来接着看。
 * 访客「先逛逛」在总览前面多一层插画星球（WORLD-ART-117，WA；页面给 stageRank 才有）：开场落到它，天上「远处的地球」进总览，总览顶栏的返回改「‹ 星球」；
 * 舞台上点一位和总览、地区层同一套开关、焦点、就近面板（标记的 data-cluster 是「stage:宠物号」）；在哪一层也记在 sessionStorage，从主页回来落回那一层。
 * 只读：数据由页面读好传进来（公开居民图层），这里不发任何请求。
 */

/**
 * 3D 星球（6796 的 planet3d/PlanetGlobe，默认导出）：按需加载，three 在它里面再按需加载一次。
 * 设备建不起 WebGL 时根本不去加载它，直接 2D 占位；加载中先显示 2D 占位（Suspense）；它回调 onUnavailable 就退回 2D 占位并且不再试。
 * onUnavailable 在开发模式的 StrictMode 下可能回调两次：退回只是把 globeDown 设成 true，重复调用没有副作用。
 */
const PlanetGlobe = lazy(() => import("../planet3d/PlanetGlobe"));

const GLOBE_VIEW_STORE = "petsoul:public-world:v1:globe-view";

/**
 * 宇宙开场（UI-SPACE-01a；用户 10-08：「这个世界……是在浩瀚宇宙中的一个世界……点击地球和这个世界会有一种穿梭感」）：
 * - 这次打开第一次进 /world、并且没有存着地区层时才放；看过就记在 sessionStorage，同一次打开里回来直接落地（2531 评审第 3 节）；
 * - 开场时页面只留返回和品牌；2.5 秒后星球下方浮出“轻点星球，出发”和“出发”“跳过”（按钮一开始就在，键盘和读屏不用等，聚焦时立刻显出来）；
 * - 落地（6796 的 onLanded，只回调一次）以后，入口、人数、状态、新鲜事依次浮上来；减少动态时直接出现；
 * - 3D 按需加载、还没下完时：开场这一次垫宇宙图和静态星球（6796 R1 v2/planet-still.png，大小位置对着 3D 远景），不先闪一下 2D 示意图；
 * - 没有 WebGL 或星球退回 2D：直接当已落地，入口全给出来（2531 第 6 节）；总览照旧是 2D 示意图（它可点、可缩放）。
 * 读存储失败按“看过”处理：宁可不放开场，也不让页面卡在宇宙里。
 */
const SPACE_SEEN_STORE = "petsoul:public-world:v1:space-seen";
const SPACE_FAR_TEXTURE = "/ui-assets/OBS-SPACE-01/v1/planet-night-2048.png";
const SPACE_BACKDROP = "/ui-assets/OBS-SPACE-01/v1/space-backdrop.jpg";
/** UI-SPACE-01b：首访自动拉近的时长（用户 10-08「1–2 秒」；REQ13 §5 约 1.2–2 秒） */
export const SPACE_AUTO_MS = 1600;

function readSpaceSeen(): boolean {
  try {
    return window.sessionStorage.getItem(SPACE_SEEN_STORE) === "1";
  } catch {
    return true;
  }
}

function readGlobeView(): GlobeView | null {
  try {
    const raw = window.sessionStorage.getItem(GLOBE_VIEW_STORE);
    const v: unknown = raw ? JSON.parse(raw) : null;
    if (!v || typeof v !== "object") return null;
    const { lat, lng, distance } = v as Record<string, unknown>;
    return typeof lat === "number" && typeof lng === "number" && typeof distance === "number" && [lat, lng, distance].every(Number.isFinite) ? { lat, lng, distance } : null;
  } catch {
    return null;
  }
}

const STORE = "petsoul:public-world:v1:region";
/** 从就近面板点「认识 TA」进居民主页时记下是哪一位；真的回来时把那张卡重新打开，停在原处（UI-WORLD-LIVING-117-2，I 10-16） */
const NEAR_RETURN_STORE = "petsoul:public-world:v1:near-return";
/** 城市层的小角色（UI2-CITY-ACTORS-117，6796 的 ResidentActor）有一截画在钉的按钮框外：睡着的 zzz 看得见的那段（不透明度大于 0.3）最高到按钮顶上约 36px（UI-2 10-16 逐帧定格量；淡出的尾巴到约 44px，压在面板下面可以），在路上的爪印、家的小标往下约 3–5px（UI-2 10-16 无头 Chrome 实测，390、320 取最大，PROBE.json）；钉自己的小尾巴也从按钮下沿探出约 9px（public-map.css 的 .ps-pmap-pin::after），评审 R7 量到卡在钉下方时视觉上贴着尖角，所以下面按 10 留。就近面板放在钉上面时多留 ACTOR_ABOVE、放在下面时多留 ACTOR_BELOW，加在原来的 8px 之外，别压住露在框外的那截；没有小角色时也一样留（只是多一点空） */
const ACTOR_ABOVE = 36;
const ACTOR_BELOW = 10;
/** 插画星球舞台上一位的标记：data-cluster 是「stage:宠物号」（WORLD-ART-117）。不在地图的点里（shown），开关、还焦点、回来重开都按这个前缀认 */
const STAGE_KEY = "stage:";

/** earth：访客从插画星球去了地球（WORLD-ART-117）；从主页回来时落回地球，不落回星球。没有插画星球的页面不看它 */
type Saved = { region: GlobeRegion | null; focusKey: string | null; earth: boolean };

function readSaved(): Saved {
  try {
    const raw = window.sessionStorage.getItem(STORE);
    const v: unknown = raw ? JSON.parse(raw) : null;
    if (!v || typeof v !== "object") return { region: null, focusKey: null, earth: false };
    const { region, focusKey, earth } = v as { region?: GlobeRegion | null; focusKey?: unknown; earth?: unknown };
    const okRegion =
      region && typeof region === "object" && region.center && [region.center.lat, region.center.lng, region.latSpan, region.lngSpan].every((n) => typeof n === "number" && Number.isFinite(n)) && typeof region.coordSystem === "string"
        ? region
        : null;
    return { region: okRegion, focusKey: typeof focusKey === "string" ? focusKey : null, earth: earth === true };
  } catch {
    return { region: null, focusKey: null, earth: false };
  }
}

// 只记选择，不缓存居民资料或聚合快照。每次渲染都从当前图层取姓名、形象、位置和可选成员。
type Open = { clusterKey: string; originIds: string[]; from: HTMLElement | null } & ({ kind: "chooser" } | { kind: "card"; petId: string });

function canReturnFocus(element: HTMLElement | null): element is HTMLElement {
  if (!element?.isConnected || element === document.body || element.tabIndex < 0 || element.matches(":disabled")) return false;
  if (element.closest('[hidden], [inert], [aria-hidden="true"], [aria-disabled="true"]')) return false;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return false;
  }
  return true;
}

function currentCluster(key: string, clusters: readonly GlobeCluster[]): GlobeCluster | null {
  const group = mergedKeys(key).flatMap((part) => clusters.filter((cluster) => cluster.key === part));
  return group.length ? mergedCluster(group) : null;
}

function identityUnavailable(resident: PublicMapResident): boolean {
  return resident.identity?.official_state === "revoked" || resident.identity?.official_state === "missing";
}

/** 地区层的地名带本城人数：和地名同一来源（regionFocus），不借用全星球的人数胶囊（UI-REVIEW110-02）。
 *  分两段给（UI2-CITY-LAYER-117）：城市层顶栏放不下时只省略地名，人数不省；连起来读还是「西安 · 7 位」。 */
function regionWhere(clusters: readonly GlobeCluster[], region: GlobeRegion): { place: string; count: string | null } {
  const n = regionFocus(clusters, region).reduce((sum, cluster) => sum + cluster.residents.length, 0);
  return { place: regionLabel(clusters, region), count: n ? ` · ${n} 位` : null };
}

export type GuestDiscoveryFrame = {
  feed: WorldFeed | undefined; feedPending: boolean; feedFailed: boolean; onRetryFeed: () => void;
  /** 星球上开着一处的短卡或选城卡：访客的上拉面板让开、收到把手，不盖住它（UI-WORLD-LIVING-117-1 评审 R2 第 1 条） */
  cardOpen: boolean;
  /** 面板要拉起时把那张卡关上（不还焦点：焦点在面板把手上） */
  closeCard: () => void;
  /** 居民人数按钮：访客的人数进上拉面板（UI-WORLD-LIVING-117-1），不再浮在星球上 */
  count: ReactNode;
  /** 城市层（UI-WORLD-LIVING-117-2，I 10-15：在哪一层说哪一层的今天）：这一层的标识（换了一层面板从头看）、地名（和顶栏同一个）、
   *  这一层那几位（regionFocus，和顶栏人数同一批；点还在读时为 null）。地球总览、或这一层的点读不出来（说不准谁在这座城）时为 null，面板照旧说星球 */
  scope: { key: string; label: string; ids: ReadonlySet<string> | null } | null;
};

/** 点一位：页面给的就近面板（UI-WORLD-LIVING-117-2；117-2b 起登录前后都是它）拿到的东西。开关、位置、焦点、点外面关、Esc 仍由这里管，新鲜事同一个读口 */
export type NearFrame = { resident: PublicMapResident; cardRef: Ref<HTMLElement>; onClose: () => void; onBack?: () => void; feed: WorldFeed | undefined;
  /** 点「认识 TA」进主页时叫一下：记下是哪一位，回来时把这张卡重新打开（I 10-16） */
  onLeave: () => void };

export function PublicWorldMap({
  back,
  layer,
  pending,
  failed,
  onRetry,
  onOpenAll,
  onOpenUnknown,
  count = null,
  badge = null,
  entry = null,
  worldEmpty = false,
  emptyAction = null,
  discovery,
  nearCard,
  nearReturn = false,
  externalOverlayOpen = false,
  stageRank,
  planetSky,
}: {
  back: string;
  layer: PublicMapLayer | undefined;
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
  onOpenAll: ((from: HTMLElement) => void) | null;
  onOpenUnknown: ((from: HTMLElement) => void) | null;
  /** 顶栏第二行的人数胶囊（页面给；第四轮 c84a D0842-2） */
  count?: ReactNode;
  /** 演示角标（页面给）：地区层不放世界人数胶囊，角标照留（UI-REVIEW110-02） */
  badge?: ReactNode;
  /** 顶栏右边的加入 / 回家入口（页面给），缩放键排在它下面 */
  entry?: ReactNode;
  /** 名单读到了、但一位居民都不显示（删插画包：没有自己形象的不上星球）：地图空态说“这里暂时还没有可以见面的居民”（D1146-5） */
  worldEmpty?: boolean;
  /** Guest next steps, only rendered for a complete, verified globally empty world. */
  emptyAction?: ReactNode;
  /** 访客的上拉面板（落地以后贴在星球底边）；新鲜事用这里同一个读口，不另起一个。 */
  discovery?: (frame: GuestDiscoveryFrame) => ReactNode;
  /** 点一位时就地弹出的就近面板（页面给，UI-WORLD-LIVING-117-2；117-2b 起登录前后都给，I 10-16） */
  nearCard: (frame: NearFrame) => ReactNode;
  /** 这一次挂上是不是从居民主页回来的（页面算：浏览器后退，或主页「返回」带着标记）；是的话把点「认识 TA」前开着的那张就近面板重新打开 */
  nearReturn?: boolean;
  externalOverlayOpen?: boolean;
  /** 访客「先逛逛」的插画星球（WORLD-ART-117）：给了就在总览前面多一层舞台。页面按故事排序说谁今天有新鲜事（pet_id → 名次，新鲜事同一个读口传进去），
   *  舞台按它挑人（planetStageModel 的 stageCast）。不给就是原来的总览、地区两层（登录后的 /world） */
  stageRank?: (feed: WorldFeed | undefined) => ReadonlyMap<string, number>;
  /** 插画星球的公开天色（B PUBLIC-SKY-117，页面从 /public/world 读）：pending＝名单还在读，先不下判断；sky 为 null＝没给，按居民睡没睡兜底 */
  planetSky?: { pending: boolean; sky: RhythmSky | null };
}) {
  // 只画有形象的居民（6c2b-no-illustration：没形象的居民不显示）；图层按契约一定有官方形象，这里再防一道
  // 同一视图里同名的，名字后面带城市（UI-REVIEW110-04）：在这里换，头像、名单、短卡、读屏名、新鲜事里按 id 取的名字都一致
  const residents = useMemo(() => withCityOnSameName(withImage(layer?.residents ?? []).filter((resident) => !identityUnavailable(resident))), [layer]);
  const nameOf = useCallback((petId: string) => residents.find((r) => r.pet_id === petId)?.name ?? null, [residents]);
  // 途中示意（PublicMapResident.move，只到片区）：给 3D 星球画淡弧线；没有就不画
  // 途中弧线：6796 的 moveRoutes（正式类型的 move，两头同一格的不画）
  const routes = useMemo(() => moveRoutes(residents), [residents]);
  // 从新鲜事点进来的“发生在这一带”（地区层上标出来）
  const [spot, setSpot] = useState<{ lat: number; lng: number; label: string } | null>(null);
  const clusters = useMemo(() => clustersOf(residents), [residents]);
  const placedCount = residents.filter(hasPosition).length;
  const unknownCount = residents.length - placedCount;

  const savedRef = useRef<Saved | null>(null);
  if (savedRef.current === null) savedRef.current = readSaved();
  const [region, setRegion] = useState<GlobeRegion | null>(savedRef.current.region);
  // 地区层里同一格的几位错开成几处（UI-REVIEW110-04）；总览照旧按格子合成。点开、名单、回来接着看、地区层的点都用这一份
  const shown = useMemo(() => (region ? spreadClusters(clusters) : clusters), [clusters, region]);
  const [focusKey, setFocusKey] = useState<string | null>(savedRef.current.focusKey);
  // 回来接着看：只在这次挂载恢复出来的那个地区上，把镜头对准上次点开的那一处。region 的初值就是 savedRef 里那个对象；
  // 回到地球再进城、进别的城都是新对象，就不再用（UI2-CITY-CAMERA-117：原来一直留着，换城以后镜头还对着旧城那一处）
  const [returnFocus] = useState(() => savedRef.current?.region ? savedRef.current.focusKey : null);
  // 插画星球（WORLD-ART-117）：页面给了 stageRank 才有；上次停在地球或城市层（从主页回来）就落回那一层，否则落在星球
  const staged = Boolean(stageRank);
  const [stageOn, setStageOn] = useState(() => staged && !savedRef.current?.region && !savedRef.current?.earth);
  useEffect(() => {
    try {
      window.sessionStorage.setItem(STORE, JSON.stringify({ region, focusKey, ...(staged ? { earth: !stageOn } : {}) }));
    } catch {
      /* 存不下：下次从总览开始 */
    }
  }, [region, focusKey, staged, stageOn]);

  const feed = useWorldFeed();
  const feedItems = useMemo(() => feed.data?.items ?? [], [feed.data]);
  // 星球上闪一下（第六轮接 6796 的 feedToGlobe）：页面存一份已见的 event_id（跨轮询保留）；第一次读到的那页只记见过、不闪（打开页面前就有的不算“刚发生”），
  // 之后每次轮询拿到的一页里没见过的才闪，时刻用页面拿到这一页的时刻（球只闪 10 秒内的）。本页不往前翻页，所以只有第一次是 baseline。
  const seenRef = useRef<Set<string> | null>(null);
  const [flashes, setFlashes] = useState<GlobeHighlight[]>([]);
  useEffect(() => {
    if (!feed.data) return;
    const baseline = seenRef.current === null;
    const next = feedFlashes({ seen: seenRef.current ?? new Set<string>(), page: feed.data, receivedAt: Date.now(), baseline });
    seenRef.current = next.seen;
    setFlashes(next.highlights);
  }, [feed.data, feed.dataUpdatedAt]);
  // 高亮只画在有标记的地方（地图上那一格有人）；没有对应标记的不画——地图空着时不会凭空冒出一圈（第三轮 1）
  const highlights = useMemo(() => highlightsOnMarkers(flashes, clusters), [flashes, clusters]);

  const deps = useMapDeps();
  const [webgl] = useState(() => deps.webgl());
  const [globeDown, setGlobeDown] = useState(false);
  const [textureFailed, setTextureFailed] = useState(false);
  // 只记朝向、不记放大：放大去地区层后回总览或重进，球原来以放大状态打开、被画框切成直边（UI-REVIEW110-02）
  const [globeView] = useState<GlobeView | null>(() => {
    const view = readGlobeView();
    return view ? { ...view, distance: Math.max(view.distance, DEFAULT_DISTANCE) } : null;
  });
  // 宇宙开场：第一次进来、没存着地区层才放；阶段以 6796 的回调为准，“落地”只记一次
  const [spaceFirst] = useState(() => !savedRef.current?.region && !readSpaceSeen());
  const [spacePhase, setSpacePhase] = useState<PlanetSpacePhase>(spaceFirst ? "space" : "landed");
  const landed = spacePhase === "landed";
  const land = useCallback(() => {
    setSpacePhase("landed");
    try {
      window.sessionStorage.setItem(SPACE_SEEN_STORE, "1");
    } catch {
      /* 存不下：下次打开再放一遍 */
    }
  }, []);
  const space = useMemo<PlanetSpaceOptions | null>(
    // UI-SPACE-01a-R1：回访也传 space，但一建好就落地（startLanded），和首访落地是同一个画面；「首次才播开场」不变
    // UI-SPACE-01b：首访不等点（tapToEnter false），页面挂上自己调 enterSpace，时长 SPACE_AUTO_MS
    () => ({ farTextureUrl: SPACE_FAR_TEXTURE, backdropUrl: SPACE_BACKDROP, tapToEnter: false, ...(spaceFirst ? { durationMs: SPACE_AUTO_MS } : { startLanded: true }), onPhase: (phase) => (phase === "landed" ? land() : setSpacePhase(phase)), onLanded: land }),
    [spaceFirst, land],
  );
  // 星球按需加载：它挂上之前点了“出发”“跳过”，先记下，挂上就照做（跳过优先）
  const globeRef = useRef<PlanetGlobeHandle | null>(null);
  const pendingSpace = useRef<"enter" | "skip" | null>(null);
  const attachGlobe = useCallback((handle: PlanetGlobeHandle | null) => {
    globeRef.current = handle;
    const want = pendingSpace.current;
    if (handle && want) {
      pendingSpace.current = null;
      if (want === "skip") handle.skipSpace();
      else handle.enterSpace();
    }
  }, []);
  const spaceAct = (want: "enter" | "skip") => {
    // 落在插画星球：拉近由舞台自己放（PlanetStage），跳过就直接落地
    if (stageOn) {
      if (want === "skip") land();
      return;
    }
    const handle = globeRef.current;
    if (handle) {
      if (want === "skip") handle.skipSpace();
      else handle.enterSpace();
    } else if (pendingSpace.current !== "skip") pendingSpace.current = want;
  };
  // UI-SPACE-01b（用户 10-08「两全」）：访客 /world 不再放「出发／跳过」按钮，首访挂上就自动缓慢拉近落地；
  // 拉近途中按 Esc 直接落地（REQ13 §5 可跳过）；点画面见下面的跳过层。减少动态时 PlanetGlobe 自己直接落地。
  useEffect(() => {
    if (!spaceFirst || landed) return;
    spaceAct("enter");
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") spaceAct("skip");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceFirst, landed]);
  useLayoutEffect(() => { if (externalOverlayOpen) setOpen(null); }, [externalOverlayOpen]);
  const saveGlobeView = useCallback((view: GlobeView) => {
    try {
      window.sessionStorage.setItem(GLOBE_VIEW_STORE, JSON.stringify(view));
    } catch {
      /* 存不下：下次从默认朝向开始 */
    }
  }, []);

  // 短卡 / 名单
  const [open, setOpen] = useState<Open | null>(null);
  // 插画星球上谁、站哪（planetStageModel）：页面给的故事名次挑人；舞台上开着一位的卡时不重新排（keepCast），站位一样就沿用上一份（castKey）
  const stageOpen = Boolean(open?.clusterKey.startsWith(STAGE_KEY));
  const castRef = useRef<StageCast[]>([]);
  const cast = useMemo(() => {
    if (!stageOn || !stageRank) return [];
    const next = stageOpen ? keepCast(castRef.current, residents) : stageCast(residents, stageRank(feed.isSuccess ? feed.data : undefined));
    // 页面每次重画都给一个新的 stageRank（按名单、新鲜事现算，很轻）；排出来一样就沿用上一份，舞台不跟着重画
    return castKey(next) === castKey(castRef.current) && next.every((entry, i) => entry.resident === castRef.current[i]?.resident) ? castRef.current : next;
  }, [stageOn, stageRank, residents, feed.data, feed.isSuccess, stageOpen]);
  castRef.current = cast;
  // 从居民主页回来：把点「认识 TA」前开着的那张就近面板重新打开（I 10-16：用户是从那一位进去的，回来要停在原处）。
  // 记号只在挂上时读一次、读完就清；这一次不是回来（欢迎页「跳过」等别的路）就不用。记号里有那一位和当时那一处（从一处几位的名单进去的，回来照旧有「回到名单」，评审 R6 中 1）；
  // 那一位要等这一层的点读到才知道在哪一处：读不到就一直等（图层读完、那一处还没算好的那一帧不丢，R6 中 2），用户自己点开了别处才作罢
  const [nearReturnMark] = useState(() => {
    try {
      const value: unknown = JSON.parse(window.sessionStorage.getItem(NEAR_RETURN_STORE) || "null");
      if (!value || typeof value !== "object") return null;
      const { petId, clusterKey } = value as { petId?: unknown; clusterKey?: unknown };
      return typeof petId === "string" ? { petId, clusterKey: typeof clusterKey === "string" ? clusterKey : null } : null;
    } catch {
      return null;
    }
  });
  const reopenRef = useRef(nearReturn ? nearReturnMark : null);
  useEffect(() => {
    try {
      window.sessionStorage.removeItem(NEAR_RETURN_STORE);
    } catch {
      /* 存不下：本来就不会恢复 */
    }
  }, []);
  useEffect(() => {
    const want = reopenRef.current;
    if (!want) return;
    if (open) {
      reopenRef.current = null;
      return;
    }
    // 落回的是插画星球：那一位在舞台上就在舞台上重开；还没排上（名单、新鲜事还在读）就接着等，和地图上一样
    if (stageOn) {
      if (!cast.some((entry) => entry.resident.pet_id === want.petId)) return;
      reopenRef.current = null;
      setOpen({ kind: "card", petId: want.petId, clusterKey: `${STAGE_KEY}${want.petId}`, originIds: [want.petId], from: null });
      return;
    }
    const before = want.clusterKey ? currentCluster(want.clusterKey, shown) : null;
    const cluster = before && before.residents.some((resident) => resident.pet_id === want.petId) ? before
      : shown.find((group) => group.residents.some((resident) => resident.pet_id === want.petId));
    if (!cluster) return;
    reopenRef.current = null;
    setFocusKey(cluster.key);
    setOpen({ kind: "card", petId: want.petId, clusterKey: cluster.key, originIds: cluster.residents.map((resident) => resident.pet_id), from: null });
  }, [shown, open, stageOn, cast]);
  const openCluster = open ? currentCluster(open.clusterKey, shown) : null;
  const openResident = open?.kind === "card" ? residents.find((resident) => resident.pet_id === open.petId) ?? null : null;
  const openRef = useRef<Open | null>(null);
  const clustersRef = useRef(shown);
  const mapRef = useRef<HTMLElement>(null);
  // 等着替 TA 重开（那一位还没在点里）的时候，用户自己在地图上动了（点、拖、滚轮、按键，例如回到地球、换一处）就作罢：
  // 不在用户已经去看别处以后突然弹出一张卡（评审 R7 挑刺 1）
  useEffect(() => {
    const root = mapRef.current;
    if (!root || !reopenRef.current) return;
    const drop = () => {
      reopenRef.current = null;
    };
    const kinds = ["pointerdown", "keydown", "wheel"] as const;
    for (const kind of kinds) root.addEventListener(kind, drop, { capture: true, passive: true });
    return () => {
      for (const kind of kinds) root.removeEventListener(kind, drop, { capture: true });
    };
  }, []);
  const retryRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLAnchorElement>(null);
  const upRef = useRef<HTMLButtonElement>(null);
  // 地球总览上「‹ 星球」（有插画星球时代替返回键）
  const stageUpRef = useRef<HTMLButtonElement>(null);
  const upPressedRef = useRef(false);
  const cardRef = useRef<HTMLElement>(null);
  // 能挪的底图挂上来的镜头口子（城市层示意图、高德，星球总览 2D）；3D 星球上没有
  const cameraRef = useRef<MapCamera | null>(null);
  // 3D 球为开着的卡、名单转过的：origin 是第一次转之前的镜头，last 是最近转的那一下（按它判断用户后来动没动过球）
  const turnedRef = useRef<{ origin: GlobeView; last: GlobeCamera } | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  // 地区层顶部渐隐（UI-WORLD-EDGE-PINS-01）：量顶栏（「‹ 地球」、地名人数；UI2-CITY-LAYER-117 起地区层没有第二行）的底边，写给样式，点滑到它底下慢慢看不见。
  // 量到 0（没排版）不写，样式按一行算；离开地区层清掉。
  useLayoutEffect(() => {
    const root = mapRef.current;
    const row = rowRef.current;
    if (!root || !row || !region) return;
    const measure = () => {
      const bottom = row.getBoundingClientRect().bottom - root.getBoundingClientRect().top;
      if (bottom > 0) root.style.setProperty("--pmap-row-bottom", `${Math.round(bottom)}px`);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(row);
    return () => {
      observer?.disconnect();
      root.style.removeProperty("--pmap-row-bottom");
    };
  }, [region, landed]);
  // 点了「回到地球」（UI2-CITY-LAYER-117 评审）：按钮跟着城市层一起没了，焦点放到地图本身，不掉到 body（改前点「回总览」也掉到 body），
  // 也不放到返回键上（按住回车会连着离开页面）。地球总览的返回键正好在「‹ 地球」原来的位置：600ms 内不接点击，双击不会一下回到欢迎页。
  // 插画星球和地球之间来回（「远处的地球」「‹ 星球」，WORLD-ART-117）同一个做法：按的那颗跟着那一层没了，焦点放到地图本身，600ms 内顶栏不接点击
  useEffect(() => {
    if (region || !upPressedRef.current) return;
    upPressedRef.current = false;
    const root = mapRef.current;
    if (!root) return;
    root.focus({ preventScroll: true });
    root.setAttribute("data-up-guard", "");
    const timer = window.setTimeout(() => root.removeAttribute("data-up-guard"), 600);
    return () => {
      window.clearTimeout(timer);
      root.removeAttribute("data-up-guard");
    };
  }, [region, stageOn]);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const focusedSelectionRef = useRef<Open | null>(null);
  useEffect(() => {
    const rememberFocus = () => {
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body) lastFocusedRef.current = active;
    };
    rememberFocus();
    document.addEventListener("focusin", rememberFocus);
    return () => document.removeEventListener("focusin", rememberFocus);
  }, []);
  // body 本身不证明需要夺回焦点；只补回实际获得过焦点、随后被移除的节点。
  const focusLost = useCallback(() => {
    const active = document.activeElement;
    return (!active || active === document.body) && Boolean(lastFocusedRef.current && !lastFocusedRef.current.isConnected);
  }, []);
  useEffect(() => {
    openRef.current = open;
    clustersRef.current = shown;
  });
  const restoreFocus = useCallback((selection: Open | null) => {
    const focus = (target: HTMLElement | null) => {
      if (!canReturnFocus(target)) return false;
      target.focus({ preventScroll: true });
      return document.activeElement === target;
    };
    const from = selection?.from ?? null;
    // 新鲜事等非地图入口仍回原控件；地图节点则必须用当前合法居民重新定位，不能依赖旧 DOM。
    if (from && !from.matches(".ps-pmap-marker") && focus(from)) return;
    const markers = [...(mapRef.current?.querySelectorAll<HTMLButtonElement>(".ps-pmap-marker[data-cluster]") ?? [])];
    const ids = selection ? [...(selection.kind === "card" ? [selection.petId] : []), ...selection.originIds] : [];
    for (const id of ids) {
      // 插画星球上：那一位自己的那颗（data-cluster 是「stage:宠物号」，不在地图的点里）
      const own = markers.find((node) => node.dataset.cluster === `${STAGE_KEY}${id}` && canReturnFocus(node));
      if (own && focus(own)) return;
      const cluster = clustersRef.current.find((group) => group.residents.some((resident) => resident.pet_id === id));
      if (!cluster) continue;
      const marker = markers.find((node) => mergedKeys(node.dataset.cluster ?? "").includes(cluster.key) && canReturnFocus(node));
      if (marker && focus(marker)) return;
    }
    // 全撤回、位置未知或当前页没有可选标记：回到可见的读取控件，否则回顶部导航；不聚焦 body。
    if (!focus(retryRef.current) && !focus(upRef.current) && !focus(stageUpRef.current)) focus(backRef.current);
  }, []);
  const close = useCallback((how: "return" | "outside" | "silent", keepTurned = how === "silent") => {
    // 3D 球为这张卡、这份名单转过的（turnedRef）：
    // - 按关上、Esc、再点同一处收起（"return"）：转回第一次转之前的朝向，存进回来接着看的那一份（评审 R2 中）；
    // - 在球上点外面关（"outside"，可能正要拖球）：不转，记的清掉；
    // - 页面自己收起（"silent"：撤掉、上拉面板拉起来；别的面板盖上来也是）、按在球以外的地方关（把手、顶栏，keepTurned）：
    //   这会儿不转，记的留着，之后按关上、Esc 时照样转回（评审 R4 中 B；用户后来自己动过球就不转，restore 自己看）
    const turned = turnedRef.current;
    if (!keepTurned) turnedRef.current = null;
    const back = how === "return" && turned ? turned.last.restore(!prefersReducedMotion(), turned.origin) : null;
    if (back) saveGlobeView(back);
    const selection = openRef.current;
    setOpen(null);
    if (how === "return") restoreFocus(selection);
    if (how === "outside") {
      window.setTimeout(() => {
        const active = document.activeElement;
        if (!active || active === document.body) restoreFocus(selection);
      }, 0);
    }
  }, [restoreFocus, saveGlobeView]);
  // 完整成功图层中缺席才能确认移除；部分分页、加载/失败不证明撤回。
  // 缺少当前资料时暂不显示浮层，但保留标识以便下一页恢复；不合并旧资料、不把缓存当撤权宽限。
  // DTO 明确给出形象撤权/缺失时，即使读口同时报部分或失败也立即关闭。
  const overlayAvailable = open?.kind === "card" ? Boolean(openResident) : Boolean(openCluster);
  const confirmedRemoval = Boolean(layer && !pending && !failed && layer.complete && !overlayAvailable);
  const deniedIdentity = open?.kind === "card" && Boolean(layer?.residents.some((resident) => resident.pet_id === open.petId && identityUnavailable(resident)));
  useEffect(() => {
    if (open && (confirmedRemoval || deniedIdentity)) {
      const ownedFocus = cardRef.current?.contains(document.activeElement);
      close(ownedFocus || focusLost() ? "return" : "silent");
    } else if (open && !overlayAvailable && focusLost()) restoreFocus(open);
  }, [open, confirmedRemoval, deniedIdentity, overlayAvailable, close, restoreFocus, focusLost]);
  useEffect(() => {
    // 只有用户开卡/换人/返回名单才改变选择对象；坐标与分页恢复不算新的打开动作。
    const explicitOpen = open !== focusedSelectionRef.current;
    focusedSelectionRef.current = open;
    if (open && overlayAvailable && (explicitOpen || focusLost())) cardRef.current?.focus({ preventScroll: true });
  });
  const openKey = open && overlayAvailable ? `${open.kind}:${openCluster?.key ?? ""}:${openResident?.pet_id ?? ""}` : null;
  useEffect(() => {
    if (!openKey) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      close("return");
    };
    const onPointer = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (cardRef.current?.contains(target)) return;
      // 点到别的标记：由标记自己换（再点一次同一处就收起）
      if (target instanceof Element && target.closest(".ps-pmap-marker")) return;
      // 3D 上按在开着的那一处光点上：和点标记一样交给星球的点选（再点同一处收起、球转回；评审 R3 中 1），不当成点外面
      const opened = openRef.current, point = event as MouseEvent;
      if (opened && target instanceof Element && target.closest(".ps-planet3d") && typeof point.clientX === "number"
        && globePinHit(globeRef.current, opened.clusterKey, target.closest(".ps-planet3d"), point.clientX, point.clientY)) return;
      // 按在球上（可能正要拖球）才清掉转过的记录；按在上拉面板的把手、顶栏这些地方，和页面自己收起一样留着（评审 R4 中 B）
      close("outside", !(target instanceof Element && target.closest(".ps-planet3d")));
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [openKey, close]);

  /** 点一处（总览的人数点、地区层的头像）：一位出短卡，几位出名单；再点同一处收起。 */
  const activate = (cluster: GlobeCluster, from: HTMLElement | null) => {
    const current = currentCluster(cluster.key, shown);
    if (!current) return;
    // 再点同一处收起。3D 上转完球，屏幕上那一团的成员可能变了（并进、分出别的格；评审 R4 中 A）：有一格相同就算同一处
    const opened = openRef.current ? currentCluster(openRef.current.clusterKey, shown) : null;
    if (opened && (opened.key === current.key || mergedKeys(opened.key).some((part) => mergedKeys(current.key).includes(part)))) {
      close("return");
      return;
    }
    setFocusKey(current.key);
    const origin = { clusterKey: current.key, originIds: current.residents.map((resident) => resident.pet_id), from };
    if (current.residents.length === 1) setOpen({ ...origin, kind: "card", petId: current.residents[0].pet_id });
    else setOpen({ ...origin, kind: "chooser" });
  };
  /**
   * 事件流里点一条（c84a D0742-3：点事件要能回到发生地）：
   * - 有片区的：切到地区层，镜头对着发生的那一带（跨度 0.1 度），标出“刚才发生在…一带”；
   * - 没片区的（相遇）：涉及的居民在地图上有公开位置就打开 TA 的短卡；都没有就不动。
   */
  const clusterOfPet = (petId: string) => shown.find((cluster) => cluster.residents.some((r) => r.pet_id === petId)) ?? null;
  const canPickFeed = (item: WorldFeedItem) => Boolean(item.area) || item.pet_ids.some((id) => clusterOfPet(id) !== null);
  const pickFromFeed = (item: WorldFeedItem, from: HTMLElement) => {
    const area = item.area;
    if (area) {
      close("silent");
      setSpot({ lat: area.lat, lng: area.lng, label: areaText(area) });
      setRegion({ center: { lat: area.lat, lng: area.lng }, latSpan: 0.1, lngSpan: 0.1, coordSystem: layer?.coord_system ?? "wgs84" });
      return;
    }
    const petId = item.pet_ids.find((id) => clusterOfPet(id) !== null);
    const cluster = petId ? clusterOfPet(petId) : null;
    const resident = cluster?.residents.find((r) => r.pet_id === petId);
    if (!cluster || !resident) return;
    setFocusKey(cluster.key);
    setOpen({ kind: "card", petId: resident.pet_id, clusterKey: cluster.key, originIds: cluster.residents.map((member) => member.pet_id), from });
  };
  const onSelect = (cluster: GlobeCluster) => activate(cluster, document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null);
  const onSelectGroup = (keys: readonly string[]): boolean => {
    // 再点同一处收起：开着的那一处在这一团里（转完球屏幕上并进了别的格；同城的也不进城市层；评审 R4 中 A）
    const opened = openRef.current ? currentCluster(openRef.current.clusterKey, shown) : null;
    if (opened && mergedKeys(opened.key).some((part) => keys.includes(part))) {
      close("return");
      return true;
    }
    const group = keys.flatMap((key) => clusters.filter((cluster) => cluster.key === key));
    if (group.length !== keys.length || group.length < 2) return false;
    const { cities, unplaced } = citiesOf(group.flatMap((cluster) => cluster.residents));
    // 同城的一团（UI-FIRST-IMPRESSION-116，I 10-15）：不飞近，直接进这座城的地区层，看得见一户一户；和选城浮层里按城进是同一条路
    if (cities.length === 1 && !unplaced) {
      const next = cityRegion(clusters, cities[0].city, layer?.coord_system ?? "wgs84");
      if (!next) return false;
      onRegion(next);
      return true;
    }
    if (cities.length < 2) return false;
    onSelect(mergedCluster(group));
    return true;
  };
  const onRegion = (next: GlobeRegion) => {
    close("silent");
    setSpot(null);
    setRegion(next);
  };
  const overview = () => {
    close("silent");
    setRegion(null);
  };
  // 插画星球 ↔ 地球（WORLD-ART-117）：天上「远处的地球」去地球总览（已落地，不再放开场），地球顶栏「‹ 星球」回来；焦点放到地图本身（见上面 upPressedRef 那段）
  const toEarth = () => {
    close("silent");
    upPressedRef.current = true;
    setStageOn(false);
  };
  const toStage = () => {
    close("silent");
    upPressedRef.current = true;
    setStageOn(true);
  };
  // 舞台上点一位：出就近面板（一处只有这一位，没有名单）；再点同一位收起
  const pickOnStage = (resident: PublicMapResident, from: HTMLElement) => {
    if (openRef.current?.kind === "card" && openRef.current.petId === resident.pet_id) {
      close("return");
      return;
    }
    setOpen({ kind: "card", petId: resident.pet_id, clusterKey: `${STAGE_KEY}${resident.pet_id}`, originIds: [resident.pet_id], from });
  };

  // 地理底图独立于居民公开位置；空态只由页面在底边说一次。
  // 空态分开如实说（c84a D1146-5），和下面的空态卡同三种：都没有公开位置——“这会儿还说不准大家在哪”；名单也是空的——“这里暂时还没有可以见面的居民”；
  // 名单有人、图层 0（候选上此刻就是这样）——“地图上暂时还看不到居民”，不拿 /public/world 名单的人数去凑地图上的点。不说“正在搬来”。
  const layerEmpty = Boolean(layer) && !pending && !failed && clusters.length === 0;
  // 双成功空态才收拢；部分分页、等待、失败和未读结果沿原呈现。
  const quietState = landed && layerEmpty && layer?.complete === true && feed.isSuccess && feedItems.length === 0;
  const quietResidents = quietState ? residents.slice(0, 3) : [];
  const emptyText = layerEmpty ? (unknownCount ? "这会儿还说不准大家在哪" : worldEmpty ? "这里暂时还没有可以见面的居民" : "地图上暂时还看不到居民") : null;
  const worldAsleep = layerEmpty && worldEmpty && unknownCount === 0 && layer?.complete === true;
  const globeProps: PlanetGlobeProps = {
    residents,
    coordSystem: layer?.coord_system ?? "wgs84",
    onSelect,
    onSelectGroup,
    onRegion,
    onUnavailable: (why) => {
      setTextureFailed(why === "texture-failed");
      setGlobeDown(true);
    },
    view: globeView,
    onViewChange: saveGlobeView,
    selectedKey: open ? focusKey : null,
    label: "星球总览：居民公开位置",
    highlights,
    routes,
    textureUrl: "/ui-assets/UI046/v1/earth-day.jpg",
    emptyText: null,
    autoRotate: !open && !region,
    earthStyle: true,
    quietControls: Boolean(discovery),
  };
  // 3D：设备有 WebGL、没退回过就用。系统要求减少动态效果时也用（6c2b 第三轮决定，放开 14:1x 那条）：6796 的球自己处理了减少动态
  // （不自转、不推近、事件不闪改成提亮），比 2D 占位更像“这个世界”；WebGL 不行时照旧退回 2D 占位。
  const use3d = webgl && !globeDown;
  // 没有 WebGL、或星球退回 2D：开场放不了，直接当已落地，入口全给出来（2531 第 6 节）
  // 落在插画星球时由舞台自己放开场、说落地（PlanetStage 的 onArrived）。设备建不起 WebGL 时和原来一样不放开场、直接落地（2531 第 6 节；
  // 舞台的拉近是整张大图的缩放加遮罩，低端设备上同样吃力）
  const canIntro = stageOn ? webgl : use3d;
  useEffect(() => {
    if (!canIntro && !landed) land();
  }, [canIntro, landed, land]);
  // 上次点开的是合成的那一处（总览 / 示意图上挨得太近合成的）：对准画标记的那一处（组里第一处）
  const returnKey = returnFocus && region === savedRef.current?.region ? (isMergedKey(returnFocus) ? mergedKeys(returnFocus)[0] : returnFocus) : null;
  // 上次点开的是总览上同一格的那一处（格心 key）、到地区层已错开：对准错开后的第一位（key 前缀对得上）
  const focusCluster = returnKey ? clusterByKey(shown, returnKey) : null;
  // 插画星球上开着的那一位：标记就是 TA 自己那颗（「stage:宠物号」），不在地图的点里找
  const openClusterKey = openResident ? (stageOpen ? open!.clusterKey : clusterOfPet(openResident.pet_id)?.key ?? null) : openCluster?.key ?? null;

  const where = region ? regionWhere(clusters, region) : null;
  // 访客面板的城市层（117-2）：地名、人数和顶栏同一批（regionFocus）；点读不出来时不说这座城（说不准谁在），面板照旧说星球
  const scope = region && where && !failed ? {
    key: `${region.center.lat},${region.center.lng},${region.latSpan},${region.lngSpan}`,
    label: where.place,
    ids: layer && !pending ? new Set(regionFocus(clusters, region).flatMap((cluster) => cluster.residents.map((resident) => resident.pet_id))) : null,
  } : null;
  // 一处几位的名单里点进来的：短卡、就近面板都多一个「回到名单」
  const backToList = open?.kind === "card" && openCluster && openCluster.residents.length > 1
    ? () => {
      // 回到名单：球为这张卡转过的先转回去，名单回到点开它时的朝向，再选谁都和头一回从名单里选一样（评审 R8 高：名单里先看了一位、球为 TA 转了一百多度，
      // 再换一位，这一位转到了地平线后面，卡量不到 TA、整张贴底盖住球）
      const turned = turnedRef.current;
      turnedRef.current = null;
      const back = turned ? turned.last.restore(!prefersReducedMotion(), turned.origin) : null;
      if (back) saveGlobeView(back);
      setOpen({ kind: "chooser", clusterKey: openCluster.key, originIds: openCluster.residents.map((member) => member.pet_id), from: open.from });
    }
    : undefined;
  // 就近面板别盖住被点的那一位、也别把按钮挤没（评审 R1–R4 高 1、R3 阻断）：量那一处的点和卡，按 nearPlacement 放在点的下面（照旧贴底）或上面，
  // 一档一档收（data-fit：生活照收成小圆，再只留名字、此刻和「认识 TA」），哪一档放得下就用哪一档；都放不下才整张照旧贴底，不限高、不裁按钮。
  // 每一档挂上再量真高（R4：减法估不准，收成小圆后字列变窄会多折行）；卡底用布局位置（offsetTop＋offsetHeight），不受进场动画的位移影响（R4 中 2）。
  // 量不到（3D 星球上的灯不是页面元素、没排版）就照旧贴底。
  // 卡里的字变了（小传读到了）再放一次：原来那一档在原来那一边还放得下就不挪（R4 中 1）；只看字和子节点，不看属性、样式，自己改的 top、data-fit 不会再触发自己。
  // 有生活照、挤的时候先轻移地图（117-2b，I 10-16 07:20 裁定）：能挪的底图（城市层的示意图和高德、星球总览的 2D）把 camera 挂上来，nearPlacement 算出要挪多少就挪，
  // 把被点的那一位挪到可见区上部，下面腾出整张生活照的地方；挪到头还不够才收成宽幅。一张卡只挪一次；挪的那一小会儿不放，挪完照原定那一档放回去（prefer）。
  // 减少动态时直接跳到位。3D 星球上点不是页面元素：从星球句柄量那一处、转一点球（globeCamera，评审 R1 高 1）。能挪多少由镜头口子说（reach），放置只按它排档；挪完、口子晚到都再放一次（评审 R2）。
  // 点开以后城市层的镜头会把那一处挪到跟前，桌面上滚轮缩放也会挪：卡开着时逐帧看那一处，挪了就再放一次（不然卡停在旧位置上，又盖住它；评审 R5）
  useLayoutEffect(() => {
    const card = cardRef.current;
    const root = mapRef.current;
    if (!card || !root || open?.kind !== "card" || !openClusterKey) return;
    const key = openClusterKey;
    // 3D 球上（页面上没有那一处的标记）：量、挪都走星球句柄
    const anchor = currentCluster(key, shown);
    const host3d = () => root.querySelector(".ps-planet3d");
    let placed: { fit: NearFit; above: boolean } | null = null;
    let seen = "";
    let panned = false;
    let settleUntil = 0;
    const settling = () => typeof performance !== "undefined" && performance.now() < settleUntil;
    const markerOf = () => [...root.querySelectorAll<HTMLElement>(".ps-pmap-marker[data-cluster]")].find((node) => mergedKeys(node.dataset.cluster ?? "").includes(key));
    // 镜头口子（平面底图挂在 cameraRef 上；3D 球上用星球句柄，见下面 globeCamera）。能往上挪多少：平面不限、3D 球按 reach()、口子没准备好（高德还没读好）是 0（评审 R2 阻断、高）
    // 按屏幕上画那一团的那一格转：放置量的就是它（评审 R5 高：从合成那一团的名单里选人，卡记的那一团排第一的不一定是画它的那一格）
    // 转球用的那一格记下来：转多了，那位自己那一格转到地平线后面、屏幕上没有含它的光点时，改量这一格（评审 R6 高：量不到就没放，卡整张贴底盖住球）
    let turnedFor: string | null = null;
    const pinOf = (marker: HTMLElement | undefined) => marker?.getBoundingClientRect() ?? globePinRect(globeRef.current, key, host3d()) ?? (turnedFor ? globePinRect(globeRef.current, turnedFor, host3d()) : null);
    const globeNow = () => {
      if (!anchor || markerOf()) return null;
      const rep = globeRepKey(globeRef.current, key);
      const at = (rep && shown.find((cluster) => cluster.key === rep)) || anchor;
      // 被选中那位自己那一格（不是画那一团的那一格时）转的时候要留在看得见的半边（评审 R7 中 1）
      return globeCamera(globeRef.current, at, host3d(), saveGlobeView, at === anchor ? undefined : anchor);
    };
    const cameraNow = (): MapCamera | null => cameraRef.current ?? globeNow();
    const reachOf = (camera: MapCamera | null) => (camera ? (camera.reach ? Math.max(0, camera.reach()) : Number.POSITIVE_INFINITY) : 0);
    let cameraReady = false;
    // 卡已经在不挪的情况下放好、开出来过了 LATE_PAN_MS：不再为了升档挪地图、转球（评审 R7 中 2：小传 2 秒多以后读到、卡变高，又转球 100°）。
    // 口子一直没准备好的（高德晚读好、回来重开那一下，R2 高）照旧能挪
    const LATE_PAN_MS = 800;
    const openedAt = typeof performance === "undefined" ? 0 : performance.now();
    let readyBefore = false;
    const place = () => {
      if (settling()) return;
      card.style.removeProperty("top");
      card.style.removeProperty("bottom");
      card.removeAttribute("data-fit");
      const marker = markerOf();
      const box = root.getBoundingClientRect();
      const pin = pinOf(marker);
      if (!pin || !box.height || !pin.height) return;
      seen = `${pin.top - box.top},${pin.bottom - box.top}`;
      const bars = [...root.querySelectorAll<HTMLElement>(".ps-pmap__bar")].map((bar) => bar.getBoundingClientRect().bottom - box.top);
      const bottom = card.offsetTop + card.offsetHeight;
      const words = card.querySelector<HTMLElement>(".ps-near__words");
      const tall = (fit: NearFit) => {
        if (fit === "full") card.removeAttribute("data-fit");
        else card.setAttribute("data-fit", fit);
        return card.offsetHeight + (words && words.offsetParent ? words.scrollHeight - words.clientHeight : 0);
      };
      // 挪地图只为生活照（I 10-16 07:20：给下面腾出放 16:9 照片的地方）；没有照片的照旧一档一档收（评审 R1–R7 定的放法不变）
      const photo = Boolean(card.querySelector("figure.ps-near__media"));
      const fits: NearFit[] = photo ? ["full", "wide", "tight", "bare"] : ["full", "tight"];
      const sizes = fits.map((fit) => [fit, tall(fit)] as const);
      card.removeAttribute("data-fit");
      const globe = cameraRef.current ? null : globeNow();
      const camera = cameraRef.current ?? globe;
      const reach = reachOf(camera);
      cameraReady = reach > 0;
      const late = typeof performance !== "undefined" && performance.now() - openedAt > LATE_PAN_MS;
      const at = nearPlacement({ top: Math.max(0, ...bars) + 8, bottom, pinTop: pin.top - box.top - 8 - ACTOR_ABOVE, pinBottom: pin.bottom - box.top + 8 + ACTOR_BELOW, sizes, prefer: placed, canPan: photo && !panned && reach > 0 && (!readyBefore || !late), reach });
      if (reach > 0) readyBefore = true;
      placed = { fit: at.fit, above: at.top !== null };
      if (at.fit !== "full") card.setAttribute("data-fit", at.fit);
      if (at.top !== null) {
        card.style.top = `${Math.round(at.top)}px`;
        card.style.bottom = "auto";
      }
      if (at.pan && camera) {
        panned = true;
        const instant = prefersReducedMotion();
        // 挪的那一小会儿不放（直接跳到位也要等地图按新镜头画完那一帧）
        settleUntil = typeof performance === "undefined" ? 0 : performance.now() + (instant ? 60 : PAN_MS + 120);
        // 接着记还是重新记：此刻还停在上一次转出来的那条路上（holds），「转之前」接着用原来的；用户动过球、去过城市层回来换了朝向，就从这里重新记（评审 R4 中 C）
        const prior = globe && turnedRef.current?.last.holds() ? turnedRef.current.origin : null;
        // 转之前记下画那一团的那一格（一步到位转完，屏幕上那一团已经变了）
        const repNow = globe ? globeRepKey(globeRef.current, key) : null;
        camera.panBy(0, -at.pan, instant);
        if (globe) { turnedRef.current = { origin: prior ?? globe.start, last: globe }; turnedFor = repNow; }
        // 挪的时候卡先不露（评审 R1 中 4：卡一下子出在最终位置，那一位从卡底下滑上来，有一百多毫秒压在卡下面）；挪完再进场
        card.setAttribute("data-settling", "");
      }
    };
    // 那一位在球背面、量不到（评审 R9 高：从主页回来转过的记录丢了，回到名单换一位，这一位在地平线后面；新鲜事里点开背面那一位）：
    // 先一步把 TA 转到看得见的地方、存进回来接着看的镜头，再量；放不下要转的照常转
    if (!cameraRef.current && anchor && !markerOf() && !globePinRect(globeRef.current, key, host3d()) && globeBringIntoView(globeRef.current, anchor, host3d())) {
      const now = globeRef.current?.getView?.();
      if (now) saveGlobeView(now);
    }
    place();
    let frame = 0;
    const follow = () => {
      const pin = pinOf(markerOf());
      const box = root.getBoundingClientRect();
      // 挪完那一下不管那一处动没动都再放一次（挪得没要求的多、或底图没挪动，就按现在的地方重新排档，别停在「挪完」那一档盖住 TA；评审 R2 阻断）
      if (card.hasAttribute("data-settling") && !settling()) { card.removeAttribute("data-settling"); place(); }
      // 口子后来才准备好（从主页回来重开那一下底图还没挂上、高德还没读好）：准备好了再放一次（评审 R2 高：回来重开矮一档、照片被撤掉）
      else if (pin && pin.height && !settling() && (`${pin.top - box.top},${pin.bottom - box.top}` !== seen || (!cameraReady && !panned && card.querySelector("figure.ps-near__media") && reachOf(cameraNow()) > 0))) place();
      frame = requestAnimationFrame(follow);
    };
    if (typeof requestAnimationFrame === "function") frame = requestAnimationFrame(follow);
    const watch = typeof MutationObserver === "undefined" ? null : new MutationObserver(place);
    watch?.observe(card, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", place);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      watch?.disconnect();
      window.removeEventListener("resize", place);
      // 换一位、关上：同一张卡的节点可能接着用，别把「先不露」留给下一位
      card.removeAttribute("data-settling");
    };
  }, [open, openClusterKey]);
  // 插画星球的昼夜（planetStageModel 的 stageSky，I 10-16 Q2）：/public/world 给了公开天色（B PUBLIC-SKY-117）就照它，名单还在读时先不下判断；
  // 没给才兜底：在家、睡没睡说得清的居民里睡着的多就是夜；一样多或都说不清用服务器时刻按东八区算；
  // 图层读不出来时才用新鲜事那一页的服务器时刻（图层还在读时不先拿钟点判，免得读到居民以后昼夜又翻一次）。都没有就是 null（舞台只留宇宙底）。不读看的人本机时钟
  const sky = stageOn && !planetSky?.pending ? stageSky({ sky: planetSky?.sky ?? null, residents, serverTime: layer?.server_time ?? (failed && feed.isSuccess ? feed.data?.server_time ?? null : null) }) : null;
  // 星球上的顶栏配色跟天色（data-stage-sky）：开场途中四周是宇宙（深色），一律按「space」配浅色字，落地以后才跟昼夜（设计评审 R2 高：白天开场时墨色箭头、品牌字压在宇宙上看不见）
  // 地球总览（和星球上）左上那一行：返回或「‹ 星球」、品牌。星球上排在舞台前面：键盘先到顶栏，不用先走完满星球的居民（设计评审 R1 中 5）
  const overviewBar = (
    <div className="ps-world-hero__bar ps-pmap__bar" data-map-interactive>
      {staged && !stageOn ? (
        <nav className="ps-pmap__crumbs" aria-label="地图层级">
          <button ref={stageUpRef} type="button" className="ps-pmap__chip ps-pmap__chip--stage" onClick={toStage} aria-label="回到星球">
            <Icon name="back" size={16} /> 星球
          </button>
        </nav>
      ) : (
        <Link ref={backRef} to={back} className="ps-world-hero__back" aria-label="返回">
          <Icon name="back" size={20} />
        </Link>
      )}
      <BrandLogo size="compact" />
    </div>
  );
  return (
    <section ref={mapRef} className={`ps-pmap${discovery ? " ps-discovery-map" : ""}`} aria-label="星球地图：居民公开位置" tabIndex={-1} data-testid="public-map" data-layer={region ? "region" : stageOn ? "stage" : "overview"}
      data-world-tone="starfield" data-space={spacePhase} data-space-arrived={spaceFirst && landed ? "" : undefined} data-globe={use3d && !region && !stageOn ? "3d" : undefined}
      data-stage-sky={stageOn && !region ? (landed ? sky ?? "space" : "space") : undefined}>
      {/* 城市层顶上只留一个往回走的（UI2-CITY-LAYER-117；评审 UI-WORLD-AWARD-REVIEW-116 第 4 条）：「‹ 地球」回到地球，旁边是这座城和人数。
          原来左上「‹」（回上一页）、「‹ 星球日常」、第二行「‹ 回总览」三个叠在一起，看不出谁回哪一层。回列表：回到地球后照旧有「‹ 星球日常」（117-1 把列表改成上拉面板后就不用切）。
          排在地图层前面：键盘先到「‹ 地球」，不用先走完满地的头像针。 */}
      {region ? (
        <div ref={rowRef} className="ps-world-hero__bar ps-pmap__bar ps-pmap__bar--region" data-map-interactive>
          <nav className="ps-pmap__crumbs" aria-label="地图层级">
            <button ref={upRef} type="button" className="ps-pmap__chip" onClick={() => { upPressedRef.current = true; overview(); }} aria-label="回到地球">
              <Icon name="back" size={16} /> 地球
            </button>
            <span className="ps-pmap__where"><span className="ps-pmap__where-place">{where?.place}</span>{where?.count ? <span className="ps-pmap__where-count">{where.count}</span> : null}</span>
          </nav>
          {badge ? <span className="ps-pmap__bar-badge">{badge}</span> : null}
          {/* 加入星球／回到我的家在城市层排进这一行的最右边：地名有多宽由排版定，不靠猜右边留多少 */}
          {landed && entry ? <div className="ps-pmap__entry ps-pmap__entry--bar" data-map-interactive>{entry}</div> : null}
        </div>
      ) : null}
      {/* 插画星球（WORLD-ART-117）：顶栏和加入排在舞台前面，键盘先到顶栏 */}
      {stageOn && !region ? overviewBar : null}
      {stageOn && !region && landed && entry ? <div className="ps-pmap__entry" data-map-interactive>{entry}</div> : null}
      {region ? (
        <RegionLayer clusters={shown} region={region} focus={focusCluster ? { lat: focusCluster.lat, lng: focusCluster.lng } : null} openKey={openClusterKey} onActivate={activate} spot={spot} quietControls={Boolean(discovery)} paused={Boolean(openKey) || externalOverlayOpen} camera={cameraRef} />
      ) : stageOn ? (
        <PlanetStage cast={cast} sky={sky} total={residents.length} openPetId={stageOpen && open?.kind === "card" ? open.petId : null}
          paused={Boolean(openKey) || externalOverlayOpen} arriving={spaceFirst && canIntro} landed={landed} settled={!pending && !feed.isPending}
          onArrived={land} onPick={pickOnStage} onEarth={toEarth} camera={cameraRef} />
      ) : use3d ? (
        <Suspense fallback={landed ? <div className="ps-pmap__layer ps-pmap__space-wait" data-layer="overview" data-testid="space-wait" aria-hidden="true" /> : <div className="ps-pmap__layer ps-pmap__space-still" data-layer="overview" data-testid="space-still" aria-hidden="true" />}>
          <PlanetGlobe {...globeProps} space={landed && space ? { ...space, startLanded: true } : space} ref={attachGlobe} />
        </Suspense>
      ) : (
        <GlobeFallback {...globeProps} camera={cameraRef} />
      )}
      {textureFailed && !region ? <p className="ps-pmap__geography-note" role="status">地理底图暂时没加载出来，先用位置示意图看看。</p> : null}

      {landed && use3d && !region && !stageOn ? <p className="ps-pmap__theme">世界很大，慢慢相遇</p> : null}

      {/* 浮在地图边上：第一行返回与品牌（城市层是「‹ 地球」和这座城），右边加入（或回家）；第二行人数胶囊（只在地球总览）；缩放键在右边第二行起 */}
      {/* 有插画星球时（WORLD-ART-117），地球总览左上是「‹ 星球」回插画星球，返回键只在星球上（星球上的顶栏排在舞台前面，见上面） */}
      {region || stageOn ? null : overviewBar}
      {!landed ? (
        // UI-SPACE-01b：看不见的跳过层盖住整个画面，点一下直接落地；读屏、键盘也能用（可聚焦、有名字）
        <button type="button" className="ps-pmap__space-skip" data-map-interactive data-testid="space-intro" aria-label="跳过，直接看星球" onClick={() => spaceAct("skip")} />
      ) : null}
      {landed && entry && !region && !stageOn ? (
        <div className="ps-pmap__entry" data-map-interactive>
          {entry}
        </div>
      ) : null}
      {/* 访客的人数在上拉面板里（UI-WORLD-LIVING-117-1），星球上不再浮一个胶囊 */}
      {landed && !region && !discovery && count && !(layerEmpty && worldEmpty && unknownCount === 0 && layer?.complete === true) ? (
        <div className="ps-pmap__row" data-map-interactive>
          {count}
        </div>
      ) : null}

      {/* 状态：读着、读不出来（可重试）、读到了但地图上还没有人、没读完 —— 读不出来和“没有人”分开说 */}
      {!landed ? null : pending ? (
        <p className="ps-pmap__state ps-pmap__state--chip" role="status">
          正在看大家在哪…
        </p>
      ) : failed ? (
        <div className="ps-pmap__state ps-pmap__state--card" role="alert" data-state="failed" data-map-interactive>
          <p className="ps-pmap__state-title">{stageOn ? "星球上的居民暂时没读出来" : "地图上的居民暂时没读出来"}</p>
          <p>可能是网络一时不顺，等一下再试试。</p>
          <button ref={retryRef} type="button" className="ps-pmap__state-btn" onClick={onRetry}>
            再试一次
          </button>
        </div>
      ) : stageOn && layer && !cast.length ? (
        // 插画星球上一位都排不上（WORLD-ART-117）：不看公开位置（舞台不按坐标放人），只看有没有能上图的居民
        <div className={`ps-pmap__state ps-pmap__state--card ps-pmap__state--empty${worldAsleep ? " is-asleep" : ""}`} role="status" data-state="empty" data-map-interactive>
          {worldAsleep ? <p className="ps-pmap__asleep-kicker">星球还在醒来</p> : null}
          <p className="ps-pmap__state-title">{residents.length ? "这会儿还说不准大家在做什么" : worldEmpty ? "这里暂时还没有可以见面的居民" : "星球上暂时还看不到居民"}</p>
          {(residents.length && onOpenAll) || !layer.complete ? (
            <div className="ps-pmap__state-actions">
              {residents.length && onOpenAll ? (
                <button type="button" className="ps-pmap__state-btn" onClick={(event) => onOpenAll(event.currentTarget)}>
                  认识星球上的居民
                </button>
              ) : null}
              {layer.complete ? null : (
                <button ref={retryRef} type="button" className="ps-pmap__state-btn is-quiet" onClick={onRetry}>
                  再试一次
                </button>
              )}
            </div>
          ) : null}
          {worldAsleep && emptyAction ? <div className="ps-pmap__state-actions ps-pmap__asleep-actions">{emptyAction}</div> : null}
        </div>
      ) : layer && !placedCount && !stageOn ? (
        <div className={`ps-pmap__state ps-pmap__state--card ps-pmap__state--empty${quietState ? " is-quiet-state" : ""}${worldAsleep ? " is-asleep" : ""}`} role="status" data-state="empty" data-testid={quietState ? "planet-quiet-state" : undefined} data-map-interactive>
          {worldAsleep ? <p className="ps-pmap__asleep-kicker">星球还在醒来</p> : null}
          <p className="ps-pmap__state-title">{emptyText}</p>
          {worldEmpty && unknownCount === 0 ? null : <p>{unknownCount ? "居民们的位置公开以后，会出现在地图上。" : onOpenAll ? "居民们公开位置以后，会出现在这里。可以先认识一下星球上的居民。" : "居民们公开位置以后，会出现在这里。"}</p>}
          {quietResidents.length ? (
            <ul className="ps-pmap__quiet-residents" aria-label="可以先认识的居民">
              {quietResidents.map((resident) => (
                <li key={resident.pet_id}>
                  <span aria-hidden="true"><MapPortrait resident={resident} size={36} /></span>
                  <span>{resident.name}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {(unknownCount && onOpenUnknown) || onOpenAll || !layer.complete ? (
          <div className="ps-pmap__state-actions">
            {unknownCount && onOpenUnknown ? (
              <button type="button" className="ps-pmap__state-btn" onClick={(event) => onOpenUnknown(event.currentTarget)}>
                {quietState ? "看看星球上的居民" : <>暂时不知道在哪（{unknownCount} 位）</>}
              </button>
            ) : onOpenAll ? (
              <button type="button" className="ps-pmap__state-btn" onClick={(event) => onOpenAll(event.currentTarget)}>
                认识星球上的居民
              </button>
            ) : null}
            {/* 名单完整读到、只是没有人：不放重读（I 10-13 定 A：再读也一样；焦点照兜底落到返回键）。没读完还空着，才给「再试一次」 */}
            {layer.complete ? null : (
              <button ref={retryRef} type="button" className="ps-pmap__state-btn is-quiet" onClick={onRetry}>
                再试一次
              </button>
            )}
          </div>
          ) : null}
          {worldAsleep && emptyAction ? <div className="ps-pmap__state-actions ps-pmap__asleep-actions">{emptyAction}</div> : null}
        </div>
      ) : layer && !layer.complete ? (
        <div className="ps-pmap__state ps-pmap__state--chip is-partial" role="status" data-state="partial" data-map-interactive>
          <span>
            只读到一部分居民（{residents.length}/{Math.max(layer.total, residents.length)}）
          </span>
          <button ref={retryRef} type="button" className="ps-pmap__chip-btn" onClick={onRetry}>
            再试一次
          </button>
        </div>
      ) : null}

      {landed && !quietState && !discovery ? <WorldFeedPanel feed={feed.data} pending={feed.isPending} failed={feed.isError} onRetry={() => void feed.refetch()} canPick={canPickFeed} onPick={pickFromFeed} nameOf={nameOf} onOpen={() => close("silent")} /> : null}
      {/* 访客：「今天星球上发生的事」上拉面板（UI-WORLD-LIVING-117-1）。面板讲的是居民名单里的事，地图上暂时没有点（quietState）也照样有；
          只有世界整个是空的（名单读完一位都没有）才由星球上的空态卡说话，不再叠一个面板 */}
      {landed && discovery && !(quietState && worldEmpty) ? discovery({ feed: feed.isSuccess ? feed.data : undefined, feedPending: feed.isPending, feedFailed: feed.isError, onRetryFeed: () => void feed.refetch(), count, cardOpen: Boolean((open?.kind === "chooser" && openCluster) || (open?.kind === "card" && openResident)), closeCard: () => close("silent"), scope }) : null}

      {open?.kind === "chooser" && openCluster ? (
        <SpotChooser
          residents={openCluster.residents}
          merged={isMergedKey(openCluster.key)}
          onPickCity={(city) => {
            // 4b（I 10-14）：合成那一盏里按城选，进那座城的地区层；和点新鲜事进片区同一条路，直接切过去、不飞
            const next = cityRegion(clusters, city, layer?.coord_system ?? "wgs84");
            if (next) onRegion(next);
          }}
          cardRef={cardRef}
          onClose={() => close("return")}
          onPick={(resident) => {
            if (openCluster.residents.some((current) => current.pet_id === resident.pet_id)) {
              setOpen({ ...open, kind: "card", petId: resident.pet_id });
            }
          }}
        />
      ) : open?.kind === "card" && openResident ? (
        nearCard({
          resident: openResident, cardRef, onClose: () => close("return"), onBack: backToList, feed: feed.isSuccess ? feed.data : undefined,
          onLeave: () => {
            try {
              window.sessionStorage.setItem(NEAR_RETURN_STORE, JSON.stringify({ petId: openResident.pet_id, clusterKey: open.clusterKey }));
            } catch {
              /* 存不下：回来时不恢复这张卡 */
            }
          },
        })
      ) : null}
    </section>
  );
}
